// Resolves a story id to a PlayableStory: the bundled built-in, or a story
// from the Supabase repository. For remote stories the engine-facing data
// (floors, scene colors, toasts) is derived from the guided model.

import { getSupabase } from "@/lib/supabase";
import type { StoryRow } from "@/lib/api";
import { BUILTIN_STORY_ID, createBuiltinStory } from "@/story/builtin";
import type { StoryModel } from "@/story/model";
import type { PlayableStory } from "@/story/playable";
import type { FloorNode } from "@/roguelike/floorPool";

export class StoryLoadError extends Error {}

export function storyRowToPlayable(row: StoryRow): PlayableStory {
  const model = (row.model ?? {}) as StoryModel;
  const schema = row.schema ?? "linear";

  const floors: FloorNode[] =
    schema === "roguelike"
      ? (model.scenes ?? [])
          .filter((s) => s.kind === "floor")
          .map((s) => ({
            path: s.id,
            name: s.title || s.id,
            tier: (s.tier ?? 1) as 1 | 2 | 3,
          }))
      : [];

  const sceneColors: Record<string, string> = {};
  for (const scene of model.scenes ?? []) {
    if (scene.color && scene.sceneKey)
      sceneColors[scene.sceneKey] = scene.color;
  }

  const messages: Record<string, string> = {
    boot: "COMMENCE",
    ...(model.messages ?? {}),
  };

  let inkJson: string;
  if (typeof row.ink_json === "string") {
    inkJson = row.ink_json;
  } else if (row.ink_json) {
    inkJson = JSON.stringify(row.ink_json);
  } else {
    throw new StoryLoadError(
      `Story "${row.title}" has not been compiled yet (no ink_json).`
    );
  }

  return {
    id: row.id,
    title: row.title ?? "Untitled story",
    schema,
    inkJson,
    floors,
    sceneColors,
    messages,
  };
}

export async function loadPlayableStory(
  storyId: string
): Promise<PlayableStory> {
  if (storyId === BUILTIN_STORY_ID) return createBuiltinStory();

  const sb = getSupabase();
  if (!sb) {
    if (storyId.startsWith("builtin")) return createBuiltinStory();
    throw new StoryLoadError(
      "Supabase is not configured; only the built-in story is available."
    );
  }

  const { data, error } = await sb
    .from("stories")
    .select("*")
    .eq("id", storyId)
    .maybeSingle();
  if (error || !data) {
    throw new StoryLoadError(`Could not load story ${storyId}.`);
  }
  return storyRowToPlayable(data as StoryRow);
}
